import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Statistic, Tag, Button, Space, Divider } from 'antd';
import { 
  RobotOutlined, 
  FolderOutlined, 
  FileTextOutlined, 
  UserOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  ApiOutlined,
  SafetyOutlined,
  RocketOutlined,
  BookOutlined,
  LinkOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Home = () => {
  const navigate = useNavigate();
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalCases: 0,
    totalStandards: 0,
    totalDocuments: 0,
    totalPolicies: 0,
    totalExperts: 0,
    totalWebsites: 0
  });

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/stats`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const newFeatures = [
    { 
      icon: <ThunderboltOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: '智能方案生成',
      description: '基于AI技术，快速生成专业的水土保持方案',
      status: 'new'
    },
    { 
      icon: <DatabaseOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: '行业数据库',
      description: '海量工程案例和技术标准，随时查阅',
      status: 'hot'
    },
    { 
      icon: <ApiOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: '智能审核',
      description: '自动检测方案中的问题和风险点',
      status: 'updated'
    },
    { 
      icon: <SafetyOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: '合规性检查',
      description: '确保方案符合最新法规标准',
      status: 'new'
    },
  ];

  const getFeatureTag = (status) => {
    const tags = {
      new: { color: 'red', text: '新功能' },
      hot: { color: 'orange', text: '热门' },
      updated: { color: 'blue', text: '更新' },
    };
    const tag = tags[status] || { color: 'default', text: status };
    return <Tag color={tag.color}>{tag.text}</Tag>;
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ marginBottom: '12px' }}>
          欢迎使用水保数联智能服务平台
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: 0 }}>
          以AI智能体为核心，为全国工程水保设计人员提供方案编写、审核等智能化服务的专业平台
        </Paragraph>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} md={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="工程案例"
              value={stats.totalCases}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="技术标准"
              value={stats.totalStandards}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="专家智库"
              value={stats.totalExperts}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="规范文档"
              value={stats.totalDocuments}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="政策法规"
              value={stats.totalPolicies}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="重点网站"
              value={stats.totalWebsites}
              prefix={<LinkOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} md={24}>
          <Card 
            title={
              <Space>
                <RocketOutlined />
                <span>最新功能</span>
              </Space>
            }
            extra={<Button type="link" onClick={() => navigate('/login')}>查看全部</Button>}
          >
            <Row gutter={[16, 16]}>
              {newFeatures.map((feature, index) => (
                <Col xs={24} sm={12} md={6} key={index}>
                  <Card 
                    size="small" 
                    hoverable
                    style={{ height: '100%' }}
                  >
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flexShrink: 0 }}>
                        {feature.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <Text strong>{feature.title}</Text>
                          {getFeatureTag(feature.status)}
                        </div>
                        <Paragraph style={{ fontSize: '12px', color: '#666', marginBottom: 0 }}>
                          {feature.description}
                        </Paragraph>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      <Divider />

      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ marginBottom: '24px' }}>
          工作模块
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size="small"
              hoverable 
              style={{ height: '100%', cursor: 'pointer' }}
              onClick={() => navigate('/agent')}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flexShrink: 0 }}>
                  <RobotOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: '14px' }}>智能体工作台</Text>
                  <Paragraph style={{ fontSize: '12px', color: '#666', marginBottom: 0, marginTop: '4px' }}>
                    AI辅助方案编写与审核
                  </Paragraph>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size="small"
              hoverable 
              style={{ height: '100%', cursor: 'pointer' }}
              onClick={() => navigate('/projects')}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flexShrink: 0 }}>
                  <FolderOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: '14px' }}>项目管理</Text>
                  <Paragraph style={{ fontSize: '12px', color: '#666', marginBottom: 0, marginTop: '4px' }}>
                    个人项目资料管理
                  </Paragraph>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size="small"
              hoverable 
              style={{ height: '100%', cursor: 'pointer' }}
              onClick={() => navigate('/industry')}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flexShrink: 0 }}>
                  <FileTextOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: '14px' }}>行业数据</Text>
                  <Paragraph style={{ fontSize: '12px', color: '#666', marginBottom: 0, marginTop: '4px' }}>
                    工程案例与技术标准
                  </Paragraph>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size="small"
              hoverable 
              style={{ height: '100%', cursor: 'pointer' }}
              onClick={() => navigate('/experts')}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flexShrink: 0 }}>
                  <UserOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: '14px' }}>专家智库</Text>
                  <Paragraph style={{ fontSize: '12px', color: '#666', marginBottom: 0, marginTop: '4px' }}>
                    专家咨询与经验分享
                  </Paragraph>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Home;
